import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'

/**
 * Toasts (F4): short messages that slide up from the bottom of the content area and go away on their own.
 * For what vanishes on the next action anyway (already in this sale, no piece with that tag, voided, cancelled).
 * Errors that need reading stay inline where they are today.
 */
export interface ToastItem { id: number; text: string; kind: 'ok' | 'bad'; leaving: boolean }
interface ToastApi { toast: (text: string, opts?: { kind?: 'ok' | 'bad'; ms?: number }) => void; dismiss: (id: number) => void }
const Api = createContext<ToastApi>({ toast: () => {}, dismiss: () => {} })
const Items = createContext<ToastItem[]>([])
export const useToast = () => useContext(Api).toast

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const seq = useRef(0)
  const dismiss = useCallback((id: number) => {
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, leaving: true } : x)))
    window.setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), 180)
  }, [])
  const toast = useCallback((text: string, opts?: { kind?: 'ok' | 'bad'; ms?: number }) => {
    const id = ++seq.current
    const kind = opts?.kind ?? 'ok'
    const ms = opts?.ms ?? (kind === 'bad' ? 4000 : 2500)
    setItems((xs) => [...xs.slice(-2), { id, text, kind, leaving: false }])   // at most three on screen
    window.setTimeout(() => dismiss(id), ms)
  }, [dismiss])
  const api = useMemo(() => ({ toast, dismiss }), [toast, dismiss])
  return <Api.Provider value={api}><Items.Provider value={items}>{children}</Items.Provider></Api.Provider>
}

/** Rendered by Page inside .main, so toasts sit over the content column and not over the aside. */
export function ToastHost({ aside }: { aside: boolean }) {
  const items = useContext(Items)
  const { dismiss } = useContext(Api)
  if (items.length === 0) return null
  return (
    <div className={'toasts' + (aside ? ' toasts--aside' : '')} aria-live="polite">
      {items.map((t) => (
        <button key={t.id} type="button" className={'toast' + (t.kind === 'bad' ? ' toast--bad' : '') + (t.leaving ? ' leaving' : '')} onClick={() => dismiss(t.id)}>{t.text}</button>
      ))}
    </div>
  )
}
